import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api';
import styled from 'styled-components';
import { DesignTokens } from '../styles/DesignTokens';

// Static libraries array for useLoadScript to prevent unnecessary reloads
const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry'];

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.$fillContainer ? '100%' : '500px'};
  border-radius: ${props => props.$fillContainer ? '0' : DesignTokens.borderRadius.lg};
  overflow: ${props => props.$fillContainer ? 'visible' : 'hidden'};
  box-shadow: ${props => props.$fillContainer ? 'none' : DesignTokens.shadow.md};
  margin-bottom: ${props => props.$fillContainer ? '0' : DesignTokens.spacing.xl};
  border: ${props => props.$fillContainer ? 'none' : `1px solid ${props.theme.colors.borderLight || '#E8EAED'}`};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    height: ${props => props.$fillContainer ? '100%' : '400px'};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    height: ${props => props.$fillContainer ? '100%' : '300px'};
  }
`;

const MapHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${DesignTokens.spacing.md};
  padding: ${DesignTokens.spacing.md};
  background: linear-gradient(135deg, #E8F0FE 0%, #D2E3FC 100%);
  border-radius: ${DesignTokens.borderRadius.md};
  border-left: 4px solid #4285F4;
`;

const MapTitle = styled.h3`
  font-size: ${DesignTokens.typography.fontSize.lg};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: #1967D2;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
`;

const MapStats = styled.div`
  display: flex;
  gap: ${DesignTokens.spacing.md};
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: #5F6368;
  flex-wrap: wrap;
`;

const DayLegend = styled.div`
  display: flex;
  gap: ${DesignTokens.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
  margin-top: ${DesignTokens.spacing.sm};
  padding-top: ${DesignTokens.spacing.sm};
  border-top: 1px solid rgba(66, 133, 244, 0.2);

  .legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: ${DesignTokens.typography.fontSize.sm};
    color: #5F6368;
    padding: 4px 10px;
    background: white;
    border-radius: ${DesignTokens.borderRadius.full};
    border: 1px solid rgba(66, 133, 244, 0.2);
  }

  .legend-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid white;
    box-shadow: 0 0 0 1px rgba(0,0,0,0.1);
  }
`;

/* ─── Floating map overlay controls ─── */
const MapOverlayControls = styled.div`
  position: absolute;
  bottom: ${props => props.$bottomOffset ? `${props.$bottomOffset + 12}px` : '10px'};
  left: 10px;
  z-index: 999;
  display: flex;
  gap: 6px;
  align-items: center;
  pointer-events: auto;
  transition: bottom 0.35s cubic-bezier(0.32, 0.72, 0, 1);
`;

const MapOverlayButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: ${props => props.$active ? '#1f2937' : 'white'};
  color: ${props => props.$active ? 'white' : '#1f2937'};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  svg {
    flex-shrink: 0;
  }
`;

const MapHintLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #1f2937;
  background: white;
  padding: 4px 10px;
  border-radius: 12px;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  animation: hintFade 3s ease-out forwards;

  @keyframes hintFade {
    0%   { opacity: 0; transform: translateX(-4px); }
    15%  { opacity: 1; transform: translateX(0); }
    70%  { opacity: 1; }
    100% { opacity: 0; }
  }
`;

const DayLegendOverlay = styled.div`
  position: absolute;
  bottom: ${props => props.$bottomOffset ? `${props.$bottomOffset + 54}px` : '52px'};
  left: 10px;
  transition: bottom 0.35s cubic-bezier(0.32, 0.72, 0, 1);
  z-index: 999;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  max-width: calc(100% - 20px);
  pointer-events: none;
`;

const LegendChip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: white;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  opacity: ${props => props.$faded ? 0.5 : 1};
`;

const LegendDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$color};
`;

const LoadingState = styled.div`
  width: 100%;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.surface || '#F8F9FA'};
  border-radius: ${DesignTokens.borderRadius.lg};
  color: ${props => props.theme.colors.textSecondary || '#5F6368'};
  font-size: ${DesignTokens.typography.fontSize.base};
`;

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

// Default center for map initialization when no locations are geocoded yet
// This is a minimal fallback - coordinates should come from LLM or Google Maps Geocoding API
const getDefaultCenter = (country) => {
  // Minimal fallback: use a generic world center
  // Actual coordinates should come from:
  // 1. LLM-provided coordinates in activity.latitude/longitude
  // 2. Google Maps Geocoding API (geocodeLocations function)
  // This is only used as initial map view before geocoding completes
  return { lat: 20, lng: 0 }; // World center as fallback
};

// Custom marker icons with different colors for each day and sequence number
const getMarkerIcon = (dayIndex, isHighlighted, sequenceNumber) => {
  const colors = ['#EA4335', '#FBBC04', '#34A853', '#4285F4', '#A142F4', '#FF6D00'];
  const color = colors[dayIndex % colors.length];
  
  // Create SVG marker with number
  const svg = `
    <svg width="40" height="52" viewBox="0 0 40 52" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
        </filter>
      </defs>
      <!-- Pin shape -->
      <path d="M20 0C11.716 0 5 6.716 5 15c0 11.25 15 37 15 37s15-25.75 15-37c0-8.284-6.716-15-15-15z" 
            fill="${isHighlighted ? '#EA4335' : color}" 
            stroke="#FFFFFF" 
            stroke-width="2" 
            filter="url(#shadow)"/>
      <!-- White circle for number background -->
      <circle cx="20" cy="15" r="9" fill="#FFFFFF"/>
      <!-- Sequence number -->
      <text x="20" y="20" 
            font-family="Arial, sans-serif" 
            font-size="12" 
            font-weight="bold" 
            fill="${color}" 
            text-anchor="middle">
        ${sequenceNumber}
      </text>
    </svg>
  `;
  
  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: isHighlighted ? { width: 48, height: 62 } : { width: 40, height: 52 },
    anchor: { x: 20, y: 52 }
  };
};

// Parse and validate coordinate values
// Note: We trust LLM-provided coordinates and Google Maps Geocoding API results
// This function only ensures coordinates are valid numbers, not correcting them
const parseCoordinates = (lat, lng) => {
  if (lat == null || lng == null) return null;
  
  // Parse coordinates to numbers
  const latNum = parseFloat(lat);
  const lngNum = parseFloat(lng);
  
  // Validate: coordinates must be valid numbers within reasonable ranges
  if (isNaN(latNum) || isNaN(lngNum)) return null;
  if (latNum < -90 || latNum > 90) return null; // Latitude range: -90 to 90
  if (lngNum < -180 || lngNum > 180) return null; // Longitude range: -180 to 180
  
  // Return parsed coordinates as-is (trust LLM/Google Maps API)
  return { lat: latNum, lng: lngNum };
};

const ItineraryMap = React.forwardRef(({ itineraryData, googleMapsApiKey, onCityExtracted, fillContainer = false, activeLocationId, onMarkerSelect, activeDayIndex, overlayBottomOffset, onExpandMap, isMapExpanded, panLeftOffset = 0 }, ref) => {
  // Load Google Maps API script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [selectedMarker, setSelectedMarker] = useState(null);
  const [map, setMap] = useState(null);
  const [showAllDays, setShowAllDays] = useState(false);
  const [showHint, setShowHint] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShowHint(false), 3000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!isMapExpanded) setShowAllDays(false);
  }, [isMapExpanded]);

  // Extract and geocode locations from itinerary
  const locations = useMemo(() => {
    if (!itineraryData) return [];

    const locs = [];
    const dailyItinerary = itineraryData.dailyItinerary || itineraryData.dayByDay || [];

    dailyItinerary.forEach((day, dayIndex) => {
      const dayNum = day.day || dayIndex + 1;
      
      // Extract from morning, afternoon, evening
      const timeBlocks = [
        { activities: day.morning, time: 'Morning' },
        { activities: day.afternoon, time: 'Afternoon' },
        { activities: day.evening, time: 'Evening' },
        { activities: day.activities, time: null } // For dayByDay format
      ];

      timeBlocks.forEach(({ activities, time }) => {
        if (activities && Array.isArray(activities)) {
          activities.forEach((activity, actIndex) => {
            if (typeof activity === 'object' && activity.activity) {
              // Extract location name from activity
              const activityText = activity.activity || '';
              const descriptionText = activity.description || '';
              // Prefer the 'place' field from LLM if available (most accurate)
              let placeField = activity.place || '';
              
              // Clean up place field first - remove time prefixes like "9:00 AM - "
              placeField = placeField.replace(/^\d+:\d+\s*[AP]M\s*-\s*/i, '').trim();
              
              // Try multiple extraction strategies
              let locationName = null;
              
              // Strategy 0: PRIORITY - Use place field after cleaning (most reliable)
              if (placeField && placeField.length > 2) {
                // Extract core location from phrases like "Explore Zermatt Village"
                // Remove action verbs
                let cleaned = placeField
                  .replace(/^(Explore|Visit|Discover|Lunch|Dinner|Breakfast|Travel|Hike|Return|Check|Sunrise|Evening|Morning|Walk)\s+(at\s+|to\s+|in\s+|along\s+|on\s+)?/i, '')
                  .replace(/^(at|to|in|on|the|along)\s+/i, '')
                  .trim();
                
                // Remove generic endings that aren't place names
                cleaned = cleaned.replace(/\s+(Boat\s+Ride|Walk|Cruise|Tour|Experience|Activity)$/i, '').trim();
                
                if (cleaned.length > 2 && !cleaned.match(/^(the|a|an|on|in|at|along)\s/i)) {
                  locationName = cleaned;
                }
              }
              
              // Strategy 1: Extract from activity text - target specific patterns with expanded place types
              if (!locationName) {
                // Match: "Matterhorn Glacier Paradise", "Gornergrat Railway", "Lake Thun Boat Cruise"
                // Added Indian place types: Ghat, Mandir, Stupa, Ashram, Dargah, Fort, etc.
                const placeTypeMatch = activityText.match(/\b([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,3})\s+(Village|Railway|Glacier|Paradise|Restaurant|Lake|Gardens?|Cruise|Kulm|Ghat|Mandir|Temple|Stupa|Ashram|Dargah|Fort|Palace|Mosque|Church|Cathedral|Museum|Park|Beach|Square|Tower|Villa|Gorge|Fortress|Monastery|Archaeological\sSite|Historic\sSite|Ruins|Island|Mountains?|Valley|River|Falls?|Waterfall|Bay|Cove|Reef|Sanctuary|Reserve|Workshop|Workshops)\b/i);
                if (placeTypeMatch) {
                  locationName = (placeTypeMatch[1] + ' ' + placeTypeMatch[2]).trim();
                }
              }
              
              // Strategy 2: Look for "at/to/in [Specific Place Name]" with expanded place types
              if (!locationName) {
                const locationMatch = activityText.match(/(?:at|to|in)\s+(?:the\s+)?([A-Z][a-zA-Z\s']+(?:Beach|Park|Museum|Castle|Church|Cathedral|Palace|Square|Tower|Villa|Garden|Vineyards?|Region|Lagoon|City|Town|Village|Harbor|Harbour|Gorge|Fortress|Monastery|Temple|Archaeological\sSite|Historic\sSite|Ruins|Island|Mountains?|Valley|Lake|River|Falls?|Waterfall|Bay|Cove|Reef|Sanctuary|Reserve|Paradise|Railway|Kulm|Ghat|Mandir|Stupa|Ashram|Dargah|Fort|Mosque|Workshop|Workshops))/i);
                if (locationMatch) {
                  locationName = locationMatch[1].trim();
                }
              }
              
              // Strategy 3: Extract from "Visit/Explore [Place]" - made more flexible
              if (!locationName) {
                // First try with place types
                let visitMatch = activityText.match(/(?:Visit|Explore|Discover|Hike|Travel\s+to)\s+(?:to\s+)?(?:the\s+)?([A-Z][a-zA-Z\s']+(?:Village|Town|City|Railway|Glacier|Paradise|Lake|Gardens?|Kulm|Restaurant|Temple|Mandir|Ghat|Stupa|Ashram|Fort|Palace|Museum|Park|Workshop|Workshops))/i);
                if (visitMatch) {
                  locationName = visitMatch[1].trim();
                } else {
                  // More flexible: "Visit to [Place Name]" - extract capitalized place name (1-4 words)
                  visitMatch = activityText.match(/(?:Visit|Explore|Discover)\s+(?:to\s+)?(?:the\s+)?([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,3})(?:\s|$|,|\.)/i);
                  if (visitMatch) {
                    const candidate = visitMatch[1].trim();
                    // Exclude generic words and time indicators
                    if (!candidate.match(/^(AM|PM|Morning|Afternoon|Evening|Day|Hour|Make|Start|Begin|End|Sunrise|Boat|Ride|Walk|Cruise|Tour)$/i)) {
                      locationName = candidate;
                    }
                  }
                }
              }
              
              // Strategy 4: Extract restaurant/venue names "Lunch/Dinner at [Name]"
              if (!locationName) {
                const restaurantMatch = activityText.match(/(?:Lunch|Dinner|Breakfast)\s+at\s+(?:the\s+)?(?:a\s+)?([A-Z][a-zA-Z\s'&]+?)(?:\s|$|,|\.)/i);
                if (restaurantMatch) {
                  let name = restaurantMatch[1].trim();
                  // Keep only if it's a proper name (not generic like "Local Restaurant")
                  if (!name.match(/^(Local|Traditional|Authentic|Cozy|Beachside|Eatery)\s/i)) {
                    locationName = name;
                  }
                }
              }
              
              // Strategy 5: Extract from description field for location hints
              if (!locationName && descriptionText) {
                // Look for place names in description (e.g., "at Dashashwamedh Ghat", "in Sarnath")
                const descMatch = descriptionText.match(/(?:at|in|near|to)\s+([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+){0,3})(?:\s|$|,|\.)/i);
                if (descMatch) {
                  const candidate = descMatch[1].trim();
                  if (candidate.length > 2 && !candidate.match(/^(The|A|An|This|That|These|Those|Morning|Afternoon|Evening)$/i)) {
                    locationName = candidate;
                  }
                }
              }
              
              // Strategy 6: Extract city names from "Travel to [City]"
              if (!locationName) {
                const travelMatch = activityText.match(/Travel\s+to\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i);
                if (travelMatch) {
                  locationName = travelMatch[1].trim();
                }
              }
              
              // Strategy 7: Extract "Return to [Place]"
              if (!locationName) {
                const returnMatch = activityText.match(/Return\s+to\s+([A-Z][a-z]+)/i);
                if (returnMatch) {
                  locationName = returnMatch[1].trim();
                }
              }
              
              // Strategy 8: Extract any prominent capitalized place (last resort) - improved
              if (!locationName) {
                // Match 1-4 word capitalized phrases, but be more selective
                const capitalizedMatches = activityText.matchAll(/\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,3})\b/g);
                for (const match of capitalizedMatches) {
                  const candidate = match[1].trim();
                  // Exclude time indicators, generic words, and common activity words
                  if (!candidate.match(/^(AM|PM|Morning|Afternoon|Evening|Day|Hour|Make|Start|Begin|End|Sunrise|Boat|Ride|Walk|Cruise|Tour|Experience|Activity|Visit|Explore|Discover|Lunch|Dinner|Breakfast|Travel|Return|Check|The|A|An)$/i) &&
                      candidate.length > 2) {
                    locationName = candidate;
                    break; // Use first valid match
                  }
                }
              }
              
              // Only add if we have a specific, geocodable location name
              // Skip generic/restaurant-only locations and activities without specific places
              const genericPatterns = /^(a|the|local|beachside|nearby|traditional|authentic|cozy|charming|family|small|restaurant|lunch|dinner|breakfast|eatery|local\s+eatery)\s/i;
              const restaurantOnly = /^Restaurant\s+[A-Z]/; // Skip standalone "Restaurant X"
              
              // Skip activities that are too generic or don't have specific locations
              const skipPatterns = [
                /^(Sunrise|Evening|Morning|Boat|Ride|Walk|Cruise|Tour|Experience|Activity)$/i,
                /^(the\s+)?Ganges$/i, // "Ganges" alone is not geocodable
                /^(the\s+)?Ghats$/i, // "Ghats" alone is too generic
                /^(a|the|local|nearby)\s+(restaurant|eatery|place|spot)$/i
              ];
              
              const isGeneric = !locationName || 
                               genericPatterns.test(locationName) || 
                               locationName.length < 3 ||
                               (restaurantOnly.test(locationName) && !locationName.includes('at')) ||
                               skipPatterns.some(pattern => pattern.test(locationName));
              
              // Additional check: if activity text suggests it's not a specific location
              // (e.g., "Boat Ride on the Ganges" without a specific ghat name)
              const isActivityWithoutLocation = activityText.match(/^(Sunrise|Evening|Morning)\s+(Boat\s+Ride|Walk|Cruise)/i) &&
                                                !locationName.match(/(Ghat|Temple|Mandir|Stupa|Fort|Palace|Museum|Park)/i);
              
              if (locationName && !isGeneric && !isActivityWithoutLocation) {
                // Clean up the location name
                locationName = locationName
                  .replace(/^\d+:\d+\s*[AP]M\s*-\s*/i, '') // Remove time prefix
                  .replace(/\s+/g, ' ')
                  .trim();
                
                // Final validation: ensure it looks like a place name
                // Should have at least one capital letter and be more than 2 characters
                if (locationName.length >= 3 && /[A-Z]/.test(locationName)) {
                  // Check if already added
                  const isDuplicate = locs.some(l => l.name === locationName && l.day === dayNum);
                  
                  if (!isDuplicate) {
                    // Priority 1: Use coordinates provided by LLM (most accurate)
                    // LLM should provide latitude/longitude in the activity object
                    let position = null;
                    if (activity.latitude != null && activity.longitude != null) {
                      // Parse and validate LLM-provided coordinates
                      const parsedCoords = parseCoordinates(
                        activity.latitude,
                        activity.longitude
                      );
                      if (parsedCoords) {
                        position = parsedCoords;
                      }
                    }
                    // Priority 2: If no LLM coordinates, will use Google Maps Geocoding API in geocodeLocations()
                    
                    locs.push({
                      id: `day${dayNum}-${time || activity.time}-${actIndex}`,
                      name: locationName,
                      day: dayNum,
                      dayIndex,
                      time: time || activity.time || 'Activity',
                      activity: activityText,
                      description: activity.description || '',
                      duration: activity.duration,
                      // Use LLM-provided coordinates if available, otherwise will be geocoded dynamically
                      position: position,
                      hasCoordinates: position !== null
                    });
                  }
                }
              }
            }
          });
        }
      });

      // Also extract from places array (clean up time prefixes and extract actual place names)
      if (day.places && Array.isArray(day.places)) {
        day.places.forEach((place, placeIndex) => {
          let placeName = typeof place === 'string' ? place : place.name || '';
          
          // Clean up place name: remove time prefixes like "6:00 AM - "
          placeName = placeName.replace(/^\d+:\d+\s*[AP]M\s*-\s*/i, '').trim();
          
          // Extract actual place name from activity-like strings
          // Remove action verbs and generic activity words
          placeName = placeName
            .replace(/^(Explore|Visit|Discover|Lunch|Dinner|Breakfast|Travel|Hike|Return|Check|Sunrise|Evening|Morning|Walk)\s+(at\s+|to\s+|in\s+|along\s+|on\s+)?/i, '')
            .replace(/^(at|to|in|on|the|along)\s+/i, '')
            .replace(/\s+(Boat\s+Ride|Walk|Cruise|Tour|Experience|Activity)$/i, '')
            .trim();
          
          // Skip if too generic or too short
          const skipPatterns = [
            /^(the\s+)?Ganges$/i,
            /^(the\s+)?Ghats$/i,
            /^(a|the|local|nearby)\s+(restaurant|eatery|place|spot)$/i,
            /^(Sunrise|Evening|Morning|Boat|Ride|Walk|Cruise|Tour|Experience|Activity)$/i
          ];
          
          const isGeneric = !placeName || 
                           placeName.length < 3 ||
                           skipPatterns.some(pattern => pattern.test(placeName));
          
          if (placeName && !isGeneric && placeName.length >= 3 && /[A-Z]/.test(placeName)) {
            // Check if already added (avoid duplicates)
            const isDuplicate = locs.some(l => l.name === placeName && l.day === dayNum);
            
            if (!isDuplicate) {
              locs.push({
                id: `day${dayNum}-place-${placeIndex}`,
                name: placeName,
                day: dayNum,
                dayIndex,
                time: 'Visit',
                activity: placeName,
                description: '',
                position: null
              });
            }
          }
        });
      }
    });

    return locs;
  }, [itineraryData]);

  // Geocode locations using Google Maps Geocoding API
  // Coordinate Priority:
  // 1. LLM-provided coordinates (activity.latitude/longitude) - most accurate, already parsed in locations extraction
  // 2. Google Maps Geocoding API - geocode place names to coordinates
  // No hardcoded coordinates - all coordinates come from LLM or Google Maps API
  const [geocodedLocations, setGeocodedLocations] = useState([]);
  const [isGeocoding, setIsGeocoding] = useState(false);

  const geocodeLocations = useCallback(async () => {
    // Check if Google Maps API is loaded before proceeding
    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
      console.warn('Google Maps API not loaded yet, skipping geocoding');
      return;
    }
    
    if (!map || geocodedLocations.length > 0 || isGeocoding) return;
    
    setIsGeocoding(true);
    const geocoded = [];
    const country = itineraryData?.country || 'World';
    
    // Extract city for better geocoding accuracy
    // Try explicit city field first, then extract from description/title if available
    let city = itineraryData?.city || null;
    if (!city && itineraryData) {
      // Extract city name from description or title (e.g., "Palermo" from "Explore Palermo's...")
      const description = (itineraryData.description || itineraryData.tripSummary || '').toLowerCase();
      const title = (itineraryData.title || '').toLowerCase();
      const text = `${title} ${description}`;
      
      // Common city patterns in itinerary descriptions (expanded with Indian cities)
      const cityMatch = text.match(/\b(palermo|rome|milan|venice|florence|naples|turin|bologna|genoa|bari|catania|messina|verona|padua|trieste|brescia|taranto|prato|modena|reggio|perugia|livorno|ravenna|cagliari|foggia|rimini|salerno|ferrara|sassari|latina|giugliano|monza|syracuse|bergamo|pescara|trento|vicenza|terni|bolzano|novara|piacenza|ancona|andria|arezzo|udine|cesena|lecce|pesaro|la\s+spezia|barletta|brindisi|como|grosseto|trapani|pisa|marsala|viterbo|mantua|campobasso|potenza|cosenza|catanzaro|caserta|benevento|ragusa|varanasi|delhi|mumbai|bangalore|kolkata|chennai|hyderabad|pune|ahmedabad|jaipur|lucknow|kanpur|nagpur|indore|thane|bhopal|visakhapatnam|patna|vadodara|ghaziabad|ludhiana|agra|nashik|faridabad|meerut|rajkot|varanasi|srinagar|amritsar|allahabad|ranchi|jabalpur|gwalior|jodhpur|raipur|kota|guwahati|chandigarh|solapur|hubli|mysore|tiruchirappalli|kochi|bhavnagar|bikaner|amravati|noida|jamshedpur|bhilai|cuttack|firozabad|baroda|saharanpur|ajmer|moradabad|sangli|raigarh|jalandhar|tirunelveli|bhubaneswar|salem|warangal|mira|bhayandar|thiruvananthapuram|bhiwandi|saharanpur|guntur|amravati|bikaner|noida|jamshedpur|bhilai|cuttack|firozabad|baroda|saharanpur|ajmer|moradabad|sangli|raigarh|jalandhar|tirunelveli|bhubaneswar|salem|warangal|mira|bhayandar|thiruvananthapuram|bhiwandi|guntur)\b/i);
      if (cityMatch) {
        // Capitalize first letter
        city = cityMatch[1].charAt(0).toUpperCase() + cityMatch[1].slice(1);
        // Handle multi-word cities
        if (city.toLowerCase() === 'la spezia') {
          city = 'La Spezia';
        }
      }
    }

    for (const location of locations) {
      try {
        // Priority 1: If location already has coordinates from LLM, use them directly (most accurate)
        if (location.hasCoordinates && location.position) {
          geocoded.push({
            ...location,
            position: location.position,
            formattedAddress: location.name,
            city: city || null
          });
          continue; // Skip geocoding for this location - LLM provided coordinates
        }
        
        // Priority 2: Use Google Maps Geocoding API to convert place names to coordinates
        // Double-check that google.maps is available before using it
        if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
          console.warn('Google Maps API not available for geocoding');
          setIsGeocoding(false);
          return;
        }
        
        const geocoder = new window.google.maps.Geocoder();
        
        // Build geocoding address with city priority: "Location, City, Country"
        // This prevents generic names (like "La Galleria") from resolving to wrong cities (e.g., Rome instead of Palermo)
        let geocodingAddress = location.name;
        if (city) {
          geocodingAddress = `${location.name}, ${city}`;
        }
        if (country !== 'World') {
          geocodingAddress = city ? `${location.name}, ${city}, ${country}` : `${location.name}, ${country}`;
        }
        
        // Add country-specific region bias for better geocoding accuracy
        const regionCode = country === 'Greece' ? 'gr' :
                          country === 'Italy' ? 'it' :
                          country === 'France' ? 'fr' :
                          country === 'Spain' ? 'es' :
                          country === 'USA' ? 'us' :
                          country === 'Japan' ? 'jp' :
                          country === 'Thailand' ? 'th' :
                          country === 'India' ? 'in' :
                          country === 'Denmark' ? 'dk' :
                          country === 'United Kingdom' ? 'uk' :
                          country === 'Germany' ? 'de' :
                          country === 'Netherlands' ? 'nl' :
                          country === 'Belgium' ? 'be' :
                          country === 'Switzerland' ? 'ch' :
                          country === 'Austria' ? 'at' :
                          country === 'Portugal' ? 'pt' :
                          country === 'Norway' ? 'no' :
                          country === 'Sweden' ? 'se' :
                          country === 'Finland' ? 'fi' : undefined;
        
        const geocodingOptions = {
          address: geocodingAddress,
          region: regionCode
          // Note: Using address format "Location, City, Country" is the best way to bias results
          // Component restrictions are not available in Geocoding API (only Places API)
        };
        
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode(
            geocodingOptions,
            (results, status) => {
              if (status === 'OK' && results[0]) {
                resolve(results[0]);
              } else {
                reject(new Error(`Geocoding failed: ${status}`));
              }
            }
          );
        });

        // Extract city from address_components (if available)
        // Priority: locality (city) > administrative_area_level_2 (province/county)
        let extractedCity = null;
        if (result.address_components) {
          // First try to find locality (actual city name)
          const localityComponent = result.address_components.find(
            component => component.types.includes('locality')
          );
          
          if (localityComponent) {
            extractedCity = localityComponent.long_name;
          } else {
            // Fallback to administrative_area_level_2, but clean it up
            const adminComponent = result.address_components.find(
              component => component.types.includes('administrative_area_level_2')
            );
            if (adminComponent) {
              // Clean up administrative prefixes like "Province of", "County of", etc.
              let cityName = adminComponent.long_name;
              cityName = cityName
                .replace(/^(Province\s+of|County\s+of|Metropolitan\s+City\s+of|Municipality\s+of|City\s+of)\s+/i, '')
                .trim();
              extractedCity = cityName;
            }
          }
        }

        geocoded.push({
          ...location,
          position: {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng()
          },
          formattedAddress: result.formatted_address,
          city: extractedCity
        });

        // Add small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        // Silently handle geocoding errors - frontend will skip failed locations
        if (process.env.NODE_ENV === 'development') {
          console.error(`Failed to geocode ${location.name}:`, error);
        }
      }
    }

    setGeocodedLocations(geocoded);
    setIsGeocoding(false);

    // Extract most common city from geocoded locations and pass to parent
    if (geocoded.length > 0 && onCityExtracted) {
      const cities = geocoded
        .map(loc => loc.city)
        .filter(city => city && city.trim().length > 0);
      
      if (cities.length > 0) {
        // Find most common city (in case of multiple cities, use the most frequent one)
        const cityCounts = {};
        cities.forEach(city => {
          cityCounts[city] = (cityCounts[city] || 0) + 1;
        });
        const mostCommonCity = Object.keys(cityCounts).reduce((a, b) => 
          cityCounts[a] > cityCounts[b] ? a : b
        );
        onCityExtracted(mostCommonCity);
      }
    }
    
    // Note: fitBounds is now handled in useEffect below for better reliability
  }, [map, locations, itineraryData, geocodedLocations.length, isGeocoding, onCityExtracted]);

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  // Geocode when map is loaded and Google Maps API is available
  React.useEffect(() => {
    // Wait for Google Maps API to be loaded before geocoding
    if (!isLoaded || !window.google || !window.google.maps) {
      return;
    }

    if (map && locations.length > 0 && geocodedLocations.length === 0) {
      geocodeLocations();
    }
  }, [map, locations, geocodedLocations.length, geocodeLocations, isLoaded]);

  // Fit map bounds to show all markers when geocoding completes
  React.useEffect(() => {
    // Check if Google Maps API is loaded before using it
    if (!isLoaded || !window.google || !window.google.maps || !window.google.maps.LatLngBounds) {
      return;
    }

    if (map && geocodedLocations.length > 0 && !isGeocoding) {
      const bounds = new window.google.maps.LatLngBounds();
      geocodedLocations.forEach(loc => {
        if (loc.position) {
          bounds.extend(loc.position);
        }
      });

      // Only fit bounds if we have valid bounds
      if (!bounds.isEmpty()) {
        // Add padding so markers aren't right at the edge
        map.fitBounds(bounds, {
          top: 50,
          right: 50,
          bottom: 50,
          left: 50
        });
      }
    }
  }, [map, geocodedLocations, isGeocoding, isLoaded]);

  // When activeLocationId changes (user clicked activity in timeline), find and highlight the marker
  React.useEffect(() => {
    if (!activeLocationId || geocodedLocations.length === 0 || !map) return;

    // Find the matching geocoded location by matching activity ID pattern
    // activeLocationId format: "day{dayIndex}-{timeBlock}-{activityIdx}"
    const matchingLocation = geocodedLocations.find(loc => {
      // Try matching by location id
      if (loc.id === activeLocationId) return true;
      // Also try fuzzy match based on day index and activity index
      return false;
    });

    if (matchingLocation && matchingLocation.position) {
      setSelectedMarker(matchingLocation);
      // Smooth pan to the marker
      map.panTo(matchingLocation.position);
      // Zoom in a bit if too far out
      if (map.getZoom() < 14) {
        map.setZoom(14);
      }
      // If a left panel is open, shift the map right so the pin is visible in the uncovered area
      if (panLeftOffset > 0) {
        map.panBy(-Math.round(panLeftOffset / 2), 0);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLocationId, geocodedLocations, map, panLeftOffset]);

  // Filter markers by active day when activeDayIndex is provided (unless showAllDays is on)
  const visibleLocations = useMemo(() => {
    if (showAllDays || activeDayIndex == null) return geocodedLocations;
    return geocodedLocations.filter(loc => loc.dayIndex === activeDayIndex);
  }, [geocodedLocations, activeDayIndex, showAllDays]);

  // Fit map to visible locations when active day or showAllDays changes
  React.useEffect(() => {
    if (!isLoaded || !window.google?.maps?.LatLngBounds || !map || visibleLocations.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    visibleLocations.forEach(loc => {
      if (loc.position) bounds.extend(loc.position);
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    }
    // Clear selected marker when view changes
    setSelectedMarker(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDayIndex, showAllDays, visibleLocations.length, map, isLoaded]);

  // Get unique days and their colors - SHOW ALL DAYS even if no geocoded locations
  const uniqueDays = useMemo(() => {
    const colors = ['#EA4335', '#FBBC04', '#34A853', '#4285F4', '#A142F4', '#FF6D00'];
    
    // Get all days from the itinerary data (not just geocoded ones)
    const dailyItinerary = itineraryData?.dailyItinerary || itineraryData?.dayByDay || [];
    const allDays = dailyItinerary.map((day, index) => day.day || index + 1);
    
    // Remove duplicates and sort
    const uniqueDayNumbers = [...new Set(allDays)].sort((a, b) => a - b);
    
    return uniqueDayNumbers.map((day, index) => ({
      day,
      color: colors[index % colors.length],
      hasLocations: geocodedLocations.some(loc => loc.day === day)
    }));
  }, [itineraryData, geocodedLocations]);

  // Polyline options removed - showing only numbered pins clustered by day

  if (!googleMapsApiKey || googleMapsApiKey === 'YOUR_GOOGLE_MAPS_API_KEY') {
    return (
      <LoadingState>
        Google Maps API key not configured. Please add your API key to display the map.
      </LoadingState>
    );
  }

  if (loadError) {
    return (
      <LoadingState>
        Error loading Google Maps. Please try refreshing the page.
      </LoadingState>
    );
  }

  if (!isLoaded) {
    return (
      <LoadingState>
        Loading map...
      </LoadingState>
    );
  }

  return (
    <div ref={ref} style={fillContainer ? { height: '100%' } : { marginTop: DesignTokens.spacing.xl }}>
      {!fillContainer && (
        <MapHeader>
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <MapTitle>
                📍 Itinerary Map
              </MapTitle>
              <MapStats>
                <span>📍 {geocodedLocations.length} locations</span>
                <span>🗓️ {itineraryData?.durationDays || 0} days</span>
              </MapStats>
            </div>
            <DayLegend>
              {uniqueDays.map(({ day, color, hasLocations }) => (
                <div key={day} className="legend-item" style={{ opacity: hasLocations ? 1 : 0.5 }}>
                  <div className="legend-dot" style={{ background: color }}></div>
                  <span>Day {day}{!hasLocations && ' (no map data)'}</span>
                </div>
              ))}
            </DayLegend>
          </div>
        </MapHeader>
      )}
        <MapContainer $fillContainer={fillContainer}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={geocodedLocations.length > 0 && geocodedLocations[0]?.position
              ? geocodedLocations[0].position
              : getDefaultCenter(itineraryData?.country || 'World')}
            zoom={geocodedLocations.length > 0 ? 10 : 9}
            onLoad={onLoad}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
              zoomControl: true
            }}
          >
            {/* Markers for each location with sequence numbers - clustered by day */}
            {visibleLocations.map((location, index) => (
              location.position && (
                <Marker
                  key={location.id}
                  position={location.position}
                  icon={getMarkerIcon(location.dayIndex, selectedMarker?.id === location.id, index + 1)}
                  onClick={() => {
                    setSelectedMarker(location);
                    if (onMarkerSelect) onMarkerSelect(location);
                  }}
                  title={`${index + 1}. ${location.name}`}
                  animation={selectedMarker?.id === location.id && window.google?.maps?.Animation ? window.google.maps.Animation.BOUNCE : null}
                  label={{
                    text: '',
                    color: 'transparent'
                  }}
                />
              )
            ))}

          </GoogleMap>

          {/* Floating toggle: show all days vs current day */}
          {fillContainer && activeDayIndex != null && geocodedLocations.length > 0 && !isMapExpanded && (
            <>
              <MapOverlayControls $bottomOffset={overlayBottomOffset}>
                <MapOverlayButton
                  $active={showAllDays}
                  onClick={() => {
                    if (onExpandMap) {
                      // Mobile: always expand to full map
                      setShowAllDays(true);
                      onExpandMap();
                    } else {
                      // Desktop: toggle between all days / current day
                      setShowAllDays(prev => !prev);
                    }
                  }}
                  aria-label={showAllDays && !onExpandMap ? 'Show current day only' : 'Show full map'}
                  title={showAllDays && !onExpandMap ? 'Current day' : 'Full map'}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {showAllDays && !onExpandMap ? (
                      /* Desktop only: calendar icon to go back to single day */
                      <>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </>
                    ) : (
                      /* Globe icon */
                      <>
                        <circle cx="12" cy="12" r="10" />
                        <line x1="2" y1="12" x2="22" y2="12" />
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                      </>
                    )}
                  </svg>
                </MapOverlayButton>
                {showHint && !showAllDays && <MapHintLabel>Full map</MapHintLabel>}
              </MapOverlayControls>

              {/* Day color legend when showing all days */}
              {showAllDays && (
                <DayLegendOverlay $bottomOffset={overlayBottomOffset}>
                  {uniqueDays.map(({ day, color, hasLocations }) => (
                    <LegendChip key={day} $faded={!hasLocations}>
                      <LegendDot $color={color} />
                      Day {day}
                    </LegendChip>
                  ))}
                </DayLegendOverlay>
              )}
            </>
          )}

          {isGeocoding && (
            <div style={{
              position: 'absolute',
              top: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'white',
              padding: '8px 16px',
              borderRadius: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              fontSize: '14px',
              zIndex: 1000
            }}>
              📍 Loading locations...
            </div>
          )}
        </MapContainer>
    </div>
  );
});

export default ItineraryMap;

